/**
 * Cart Utilities
 * Shared cart integration functions for all product purchase components
 * Extracted from separate-buy-buttons-v4-no-bulk.liquid
 */

window.CartUtilities = window.CartUtilities || {};

(function(CartUtilities) {
  'use strict';

  // Configuration - All settings and constants
  const CONFIG = {
    // Selling Plans
    STAY_SELLING_PLAN_ID: document.querySelector('[data-stay-selling-plan-id]')?.dataset.staySellingPlanId || '689099145512',
    ADDON_SUBSCRIPTION_PLAN_ID: document.querySelector('[data-addon-plan-id]')?.dataset.addonPlanId || null,
    CROSS_SELL_PLAN_ID: document.querySelector('[data-cross-sell-plan-id]')?.dataset.crossSellPlanId || '2882076770',
    TRYNOW_SELLING_PLAN_ID: document.querySelector('[data-trynow-selling-plan-id]')?.dataset.trynowSellingPlanId || '691435766056',
    TRYNOW_CHECKOUTPRODUCT_VARIANT_ID: document.querySelector('[data-trynow-checkout-product-variant-id]')?.dataset.trynowCheckoutProductVariantId || '50633931555112',

    // Product Properties
    PROPERTIES: {
      FULL_PRICE: "_full_price",
      PRODUCT_TYPE: "_product_type",
      SUBSCRIPTION_PURCHASE: "_is_subscription_purchase",
      ONE_TIME_PURCHASE: "_is_one_time_purchase",
      SELLING_PLAN: "_selling_plan",
      TRY_NOW_PAIR_KEY: "_trynow_pair_key",
      TRY_NOW_PAIR_ROLE: "_trynow_pair_role",
    },
    PAIR_ROLES: {
      PRIMARY: 'primary',
      SUBSCRIPTION: 'subscription'
    }
  };

  /**
   * Create item data structure for cart
   * @param {Object} params - Item parameters
   * @param {number} params.variantId - Shopify variant ID
   * @param {number} params.quantity - Item quantity
   * @param {boolean} params.isSubscription - Whether this is a subscription purchase
   * @param {boolean} [params.isTBYB=false] - Whether this is a Try Before You Buy item
   * @param {Object} [params.properties={}] - Additional line item properties
   * @param {string|null} [params.sellingPlanId=null] - Override selling plan ID
   * @param {boolean} [params.isAddon=false] - Whether this is an add-on product
   * @returns {Object} Cart item data structure
   */
  function createItemData({variantId, quantity, isSubscription, isTBYB = false, properties = {}, sellingPlanId = null, isAddon = false}) {
    console.log('📦 [CART-UTIL] Creating item data with params:', {
      variantId,
      quantity,
      isSubscription,
      isTBYB,
      properties,
      isAddon,
        sellingPlanId
    });

    const baseProperties = {
      [CONFIG.PROPERTIES.FULL_PRICE]: "true",
      [CONFIG.PROPERTIES.PRODUCT_TYPE]: "rebrand",
      ...properties
    };

    const item = {
      id: variantId,
      quantity: quantity,
      properties: baseProperties
    };

    const resolvedSellingPlanId = sellingPlanId ||
                                  (isSubscription ? (isAddon ? CONFIG.ADDON_SUBSCRIPTION_PLAN_ID : CONFIG.STAY_SELLING_PLAN_ID) : null);

    if (resolvedSellingPlanId) {
      console.log('📦 [CART-UTIL] Adding selling plan:', resolvedSellingPlanId);
      item.selling_plan = resolvedSellingPlanId;
    }

    if (isSubscription) {
      item.properties[CONFIG.PROPERTIES.SUBSCRIPTION_PURCHASE] = true;
    }

    if (isTBYB) {
      console.log('📦 [CART-UTIL] Adding one-time purchase with TRYNOW selling plan:', CONFIG.TRYNOW_SELLING_PLAN_ID);
      item.selling_plan = CONFIG.TRYNOW_SELLING_PLAN_ID;
      item.properties[CONFIG.PROPERTIES.ONE_TIME_PURCHASE] = true;
      item.properties[CONFIG.PROPERTIES.SELLING_PLAN] = CONFIG.TRYNOW_SELLING_PLAN_ID;
    }

    console.log('📦 [CART-UTIL] Final item data:', item);
    return item;
  }

  /**
   * Add items to cart
   * @param {Object} itemsData - Cart items data with items array
   * @returns {Promise<Response>} Fetch response
   */
  async function addToCartWithItems(itemsData) {
    console.log('🛒 [CART-UTIL] Starting addToCartWithItems with data:', itemsData);

    try {
      const response = await window.addToCart(itemsData);

      console.log('🛒 [CART-UTIL] Add to cart response status:', response.status);
      console.log('🛒 [CART-UTIL] Add to cart response ok:', response.ok);

      const data = await response.json();
      console.log('🛒 [CART-UTIL] Product added to cart response:', data);

      console.log('🛒 [CART-UTIL] Checking cart display functions...');
      console.log('🛒 [CART-UTIL] updateCartDisplay available:', typeof updateCartDisplay === 'function');
      console.log('🛒 [CART-UTIL] updateCartCount available:', typeof updateCartCount === 'function');
      console.log('🛒 [CART-UTIL] toggleCartOpen available:', typeof toggleCartOpen === 'function');
      console.log('🛒 [CART-UTIL] showAddToCartMessage available:', typeof showAddToCartMessage === 'function');

      if (typeof updateCartDisplay === 'function') {
        console.log('🛒 [CART-UTIL] Fetching updated cart data...');
        const cartResponse = await fetch('/cart.js');

        if (cartResponse) {
          console.log('🛒 [CART-UTIL] Cart.js response status:', cartResponse.status);
          const updatedCart = await cartResponse.json();
          console.log('🛒 [CART-UTIL] Updated cart data:', updatedCart);

          if (typeof toggleCartOpen === 'function') {
            console.log('🛒 [CART-UTIL] Opening cart...');

            // Show loading state while cart is opening
            showEmptyCartLoading();

            toggleCartOpen();

            // Focus the checkout button after cart opens for accessibility
            setTimeout(() => {
              const checkoutButton = document.getElementById('checkoutButton');

              if (checkoutButton && !checkoutButton.disabled) {
                checkoutButton.focus();
                console.log('♿ [CART-UTIL] Focused checkout button for accessibility');

                // Announce to screen readers
                if (typeof announceToScreenReader === 'function') {
                  announceToScreenReader('Items added to cart. Focus on checkout button.', 'polite');
                }
              } else {
                // Fallback: focus close button if checkout is disabled
                const cart = document.querySelector('#main-nav');
                const closeButton = cart?.querySelector('.js-menu__close');

                if (closeButton) {
                  closeButton.focus();
                  console.log('♿ [CART-UTIL] Checkout disabled, focused close button');
                }
              }
            }, 150);
          }

          console.log('🛒 [CART-UTIL] Calling updateCartDisplay with cart:', updatedCart);
          updateCartDisplay(updatedCart);

          if (typeof updateCartCount === 'function') {
            console.log('🛒 [CART-UTIL] Updating cart count:', updatedCart.item_count);
            updateCartCount(updatedCart.item_count);
          }

          if (typeof showAddToCartMessage === 'function') {
            console.log('🛒 [CART-UTIL] Showing success message');
            showAddToCartMessage('Product added to cart!');
          }
        } else {
          console.log('🛒 [CART-UTIL] No cart response available');
        }
      } else {
        console.log('🛒 [CART-UTIL] No updateCartDisplay function, redirecting to cart page');
        window.location.href = '/cart';
      }
    } catch (error) {
      console.error('🛒 [CART-UTIL] Error adding to cart:', error);
      if (typeof showAddToCartMessage === 'function') {
        showAddToCartMessage('Error adding product to cart', 'error');
      } else {
        alert('Error adding product to cart');
      }
    }
  }

  /**
   * Update cart display after adding items
   * @returns {Promise<void>}
   */
  async function updateCartAfterAdd() {
    console.log('🛒 [CART-UTIL] Updating cart display after add');

    if (typeof updateCartDisplay === 'function') {
      console.log('🛒 [CART-UTIL] Fetching updated cart data...');
      const cartResponse = await fetch('/cart.js');
      const updatedCart = await cartResponse.json();

      // Show loading state while cart is opening
      showEmptyCartLoading();

      console.log('🛒 [CART-UTIL] Updated cart data:', updatedCart);

      if (typeof toggleCartOpen === 'function') {
        console.log('🛒 [CART-UTIL] Opening cart...');
        toggleCartOpen();

        // Focus the checkout button after cart opens for accessibility
        setTimeout(() => {
          const checkoutButton = document.getElementById('checkoutButton');

          if (checkoutButton && !checkoutButton.disabled) {
            checkoutButton.focus();
            console.log('♿ [CART-UTIL] Focused checkout button for accessibility');

            // Announce to screen readers
            if (typeof announceToScreenReader === 'function') {
              announceToScreenReader('Items added to cart. Focus on checkout button.', 'polite');
            }
          } else {
            // Fallback: focus close button if checkout is disabled
            const cart = document.querySelector('#main-nav');
            const closeButton = cart?.querySelector('.js-menu__close');

            if (closeButton) {
              closeButton.focus();
              console.log('♿ [CART-UTIL] Checkout disabled, focused close button');
            }
          }
        }, 150);

        // Schedule cart refreshes at 2 and 4 seconds after opening
        scheduleCartRefresh();
      }

      updateCartDisplay(updatedCart);

      if (typeof updateCartCount === 'function') {
        console.log('🛒 [CART-UTIL] Updating cart count:', updatedCart.item_count);
        updateCartCount(updatedCart.item_count);
      }
    }
  }

  /**
   * Show empty cart loading state
   */
  function showEmptyCartLoading() {
    const loadingState = document.querySelector('.firstday-cart__flyout-empty-cart-loading');
    const emptyState = document.querySelector('.firstday-cart__flyout-empty-cart');
    const cartContent = document.getElementById('mini_cart_form');

    if (loadingState) {
      loadingState.style.display = 'block';
    }
    if (emptyState) {
      emptyState.style.display = 'none';
    }
    if (cartContent) {
      cartContent.style = 'display: none !important';
    }

    // Disable checkout button using button-loading-utility
    if (typeof window.setButtonLoading === 'function') {
      window.setButtonLoading('checkoutButton', true);
    }

    console.log('🛒 [CART-UTIL] Showing empty cart loading state');
  }

  /**
   * Schedule cart refresh after minicart is opened
   */
  function scheduleCartRefresh() {
    console.log('🔄 [CART-UTIL] Scheduling cart refreshes and TBYB checkout product check');

    // Check and add TBYB checkout product at 1 second
    setTimeout(async () => {
      try {
        await checkAndAddTBYBCheckoutProduct();
      } catch (error) {
        console.error('🔄 [CART-UTIL] Error during TBYB checkout product check:', error);
      }
    }, 1000);

    // Refresh cart at 2 seconds
    setTimeout(async () => {
      try {
        console.log('🔄 [CART-UTIL] Executing 2-second cart refresh');
        const cartResponse = await fetch('/cart.js');
        const updatedCart = await cartResponse.json();
        if (typeof updateCartDisplay === 'function') {
          await updateCartDisplay(updatedCart);
        }
      } catch (error) {
        console.error('🔄 [CART-UTIL] Error during 2-second refresh:', error);
      }
    }, 2000);

    // Refresh cart at 4 seconds
    setTimeout(async () => {
      try {
        console.log('🔄 [CART-UTIL] Executing 4-second cart refresh');
        const cartResponse = await fetch('/cart.js');
        const updatedCart = await cartResponse.json();
        if (typeof updateCartDisplay === 'function') {
          await updateCartDisplay(updatedCart);
        }
      } catch (error) {
        console.error('🔄 [CART-UTIL] Error during 4-second refresh:', error);
      }
    }, 4000);
  }

  /**
   * Check if TBYB items exist and ensure TRYNOW_CHECKOUTPRODUCT_VARIANT_ID is in cart
   * @returns {Promise<void>}
   */
  async function checkAndAddTBYBCheckoutProduct() {
    console.log('🧪 [CART-UTIL] Checking for TBYB items and checkout product');

    try {
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      console.log('🧪 [CART-UTIL] Current cart items:', cart.items);

      // Check if any item has TRYNOW selling plan (indicates TBYB order)
      const hasTBYBItems = cart.items.some(item =>
        item.selling_plan_allocation?.selling_plan?.id === CONFIG.TRYNOW_SELLING_PLAN_ID ||
        item.properties?.[CONFIG.PROPERTIES.SELLING_PLAN] === CONFIG.TRYNOW_SELLING_PLAN_ID
      );

      console.log('🧪 [CART-UTIL] Has TBYB items:', hasTBYBItems);

    } catch (error) {
      console.error('🧪 [CART-UTIL] Error in checkAndAddTBYBCheckoutProduct:', error);
    }
  }

  /**
   * Get subscription variant ID for a product
   * @param {string|number} productId - Shopify product ID
   * @returns {Promise<string|null>} Subscription variant ID or null
   */
  async function getSubscriptionVariantId(productId) {
    if (!window.Storefront?.productSubscriptionVariantById) {
      console.log('🔍 [CART-UTIL] Storefront.productSubscriptionVariantById not available');
      return null;
    }

    try {
      console.log('🔍 [CART-UTIL] Fetching subscription variant for product:', productId);
      let subscriptionVariantId = await window.Storefront.productSubscriptionVariantById(productId);
      subscriptionVariantId = window.fromGID(subscriptionVariantId);
      console.log('🔍 [CART-UTIL] Found subscription variant:', subscriptionVariantId);
      return subscriptionVariantId;
    } catch (error) {
      console.error('🔍 [CART-UTIL] Error fetching subscription variant:', error);
      return null;
    }
  }

  /**
   * Add cross-sell items to items array
   * @param {Array} items - Current items array
   * @param {boolean} isSubscription - Whether main items are subscription
   * @param {boolean} [isTBYB=false] - Whether this is a TBYB order
   * @returns {Promise<Array>} Updated items array with cross-sell items
   */
  async function addCrossSellItems(items, isSubscription, isTBYB = false) {
    const crossSellChecked = document.getElementById('crossSellProduct')?.checked;

    if (crossSellChecked) {
      console.log('🎯 [CART-UTIL] Adding cross-sell item');
      const crossSellId = document.getElementById('crossSellId').value;
      const crossSellPlan = CONFIG.CROSS_SELL_PLAN_ID;
      console.log('🎯 [CART-UTIL] Cross-sell details:', {crossSellId, crossSellPlan, isSubscription, isTBYB});

      const crossSellPairId = `cs-${Date.now()}-${crossSellId}`;

      items.forEach(item => {
        if (!item || typeof item !== 'object') {
          return;
        }

        const itemProperties = item.properties || {};
        item.properties = {
          ...itemProperties,
          _cross_sell_pair_id: crossSellPairId,
          _has_cross_sell: 'true'
        };
      });

      // Add the main cross-sell item (with TBYB flag if applicable)
      items.push(createItemData({
        variantId: crossSellId,
        quantity: 1,
        isSubscription: isTBYB ? false : isSubscription,  // Force false for TBYB primary item
        isTBYB: isTBYB,
        sellingPlanId: (!isTBYB && isSubscription) ? crossSellPlan : null,
        properties: {
          _is_cross_sell: 'true',
          _cross_sell_pair_id: crossSellPairId
        }
      }));

      // If cross-sell is TBYB and subscription, also add its subscription variant
      if (isTBYB && isSubscription) {
        console.log('🎯 [CART-UTIL] TBYB + Subscription mode for cross-sell');

        // Get cross-sell product ID (assuming we have it stored)
        const crossSellProductId = document.getElementById('crossSellProductId')?.value;

        if (crossSellProductId) {
          const crossSellSubscriptionVariantId = await getSubscriptionVariantId(crossSellProductId);

          if (crossSellSubscriptionVariantId) {
            const pairKey = `cs-tn-${Date.now()}-${crossSellId}`;
            console.log('🎯 [CART-UTIL] Adding cross-sell subscription variant:', crossSellSubscriptionVariantId, 'with pair key', pairKey);

            // Update the original cross-sell item with TBYB properties
            const originalCrossSellItem = items[items.length - 1];
            originalCrossSellItem.properties = {
              ...originalCrossSellItem.properties,
              [CONFIG.PROPERTIES.TRY_NOW_PAIR_KEY]: pairKey,
              [CONFIG.PROPERTIES.TRY_NOW_PAIR_ROLE]: CONFIG.PAIR_ROLES.PRIMARY,
              _cross_sell_pair_id: crossSellPairId,
            };
            originalCrossSellItem.selling_plan = CONFIG.TRYNOW_SELLING_PLAN_ID;

            // Add the subscription variant for cross-sell
            items.push(createItemData({
              variantId: parseInt(crossSellSubscriptionVariantId, 10),
              quantity: 1,
              isSubscription: true,
              isTBYB: false,
              sellingPlanId: crossSellPlan,
              properties: {
                [CONFIG.PROPERTIES.TRY_NOW_PAIR_KEY]: pairKey,
                [CONFIG.PROPERTIES.TRY_NOW_PAIR_ROLE]: CONFIG.PAIR_ROLES.SUBSCRIPTION,
                _is_cross_sell: 'true',
                _cross_sell_pair_id: crossSellPairId
              }
            }));
          } else {
            console.log('🎯 [CART-UTIL] No subscription variant found for cross-sell product');
          }
        } else {
          console.log('🎯 [CART-UTIL] Cross-sell product ID not available for subscription variant lookup');
        }
      }
    }

    return items;
  }

  /**
   * Try Before You Buy - Add to cart handler
   * @param {Object} params - TBYB parameters
   * @param {number} params.variantId - Variant ID (or array for starter packs)
   * @param {number} params.quantity - Quantity
   * @param {boolean} params.isSubscription - Whether subscription is selected
   * @param {string|number} [params.productId] - Product ID for subscription variant lookup
   * @param {Object} [params.additionalProperties={}] - Additional line item properties
   * @returns {Promise<void>}
   */
  async function addToCartTBYB({variantId, quantity, isSubscription, productId, additionalProperties = {}}) {
    console.log('🧪 [CART-UTIL] Adding TBYB item:', {variantId, quantity, isSubscription, productId});

    try {
      let items = [];
      const subscriptionFlagProperties = isSubscription
        ? {[CONFIG.PROPERTIES.SUBSCRIPTION_PURCHASE]: true}
        : {};

      if (isSubscription) {
        console.log('🧪 [CART-UTIL] Dual mode detected - fetching subscription variant');

        const currentProductId = productId || getCurrentProductId();

        if (currentProductId) {
          const subscriptionVariantId = await getSubscriptionVariantId(currentProductId);

          if (subscriptionVariantId) {
            const pairKey = `tn-${Date.now()}-${variantId}`;
            console.log('🧪 [CART-UTIL] Adding subscription variant:', subscriptionVariantId, 'with STAY selling plan and pair key', pairKey);

            items.push(createItemData({
              variantId,
              quantity,
              isSubscription: false,
              isTBYB: true,
              properties: {
                ...subscriptionFlagProperties,
                ...additionalProperties,
                [CONFIG.PROPERTIES.TRY_NOW_PAIR_KEY]: pairKey,
                [CONFIG.PROPERTIES.TRY_NOW_PAIR_ROLE]: CONFIG.PAIR_ROLES.PRIMARY,
              }
            }));

            items.push(createItemData({
              variantId: parseInt(subscriptionVariantId, 10),
              quantity,
              isSubscription: true,
              isTBYB: false,
              properties: {
                [CONFIG.PROPERTIES.TRY_NOW_PAIR_KEY]: pairKey,
                [CONFIG.PROPERTIES.TRY_NOW_PAIR_ROLE]: CONFIG.PAIR_ROLES.SUBSCRIPTION,
              }
            }));
          } else {
            console.log('🧪 [CART-UTIL] No subscription variant found in metafield');
            items.push(createItemData({
              variantId,
              quantity,
              isSubscription: false,
              isTBYB: true,
              properties: {...subscriptionFlagProperties, ...additionalProperties},
            }));
          }
        } else {
          console.log('🧪 [CART-UTIL] Could not determine current product ID');
          items.push(createItemData({
            variantId,
            quantity,
            isSubscription: false,
            isTBYB: true,
            properties: {...subscriptionFlagProperties, ...additionalProperties},
          }));
        }
      } else {
        items.push(createItemData({
          variantId,
          quantity,
          isSubscription: false,
          isTBYB: true,
          properties: {...subscriptionFlagProperties, ...additionalProperties},
        }));
      }

      // Add cross-sell items using shared function (with TBYB flag)
      items = await addCrossSellItems(items, isSubscription, true);

      console.log('🧪 [CART-UTIL] Final items to add:', items);

      const response = await window.addToCart({ items });

      if (typeof window.trynow?.addToCartClicked === 'function') {
        await window.trynow.addToCartClicked();
      }

      await response.json();

      // After addToCartClicked is complete, update cart using shared function
      await updateCartAfterAdd();
    } catch (error) {
      console.error("🧪 [CART-UTIL] Error adding TryNow item to cart:", error);
      throw error;
    }
  }

  /**
   * Get current product ID from various sources
   * @returns {string|number|null} Product ID or null
   */
  function getCurrentProductId() {
    return window.meta?.product?.id ||
      window.ShopifyAnalytics?.meta?.product?.id ||
      document.querySelector('[data-product-id]')?.dataset?.productId;
  }

  // Expose public API
  CartUtilities.createItemData = createItemData;
  CartUtilities.addToCartWithItems = addToCartWithItems;
  CartUtilities.updateCartAfterAdd = updateCartAfterAdd;
  CartUtilities.addCrossSellItems = addCrossSellItems;
  CartUtilities.addToCartTBYB = addToCartTBYB;
  CartUtilities.getSubscriptionVariantId = getSubscriptionVariantId;
  CartUtilities.getCurrentProductId = getCurrentProductId;
  CartUtilities.CONFIG = CONFIG;

})(window.CartUtilities);
